import React, { useState, useEffect } from 'react';
import { NEUTRAL, STATUS_COLORS, onAccent } from './designTokens';

// T-16: Dagens tider som ljus agenda under Bokningars mörka huvud (tidigare en
// helmörk box, T-12). Rubrik, dag-navigering och "Blockera dagen" ligger i huvudet
// (Bookings.js). Används i kalenderns dagläge och överst i fliken Kommande.

export const DAY_NAMES = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];
export const MONTHS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export function isoWeek(d) {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dow = x.getUTCDay() || 7;
  x.setUTCDate(x.getUTCDate() + 4 - dow);
  const y0 = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil(((x - y0) / 864e5 + 1) / 7);
}
const hm = (t) => (t || '').slice(0, 5);
const minutes = (t) => { const [h, m] = hm(t || '00:00').split(':').map(Number); return h * 60 + m; };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const initials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

// Rader för en dag: bokningar + lediga tider + blockerade tider, i tidsordning.
export function buildDayRows({ date, schedule, slots, dayBookings, dayBlocked, isTimeBooked, isSlotBlocked }) {
  const dateStr = ymd(date);
  const blockedAll = dayBlocked.find(bt => bt.all_day) || null;
  const rows = dayBookings.map(b => ({ kind: 'booking', key: `b:${b.id}`, start: hm(b.start_time), end: hm(b.end_time), b }));
  if (schedule && !blockedAll) {
    slots
      .filter(s => !isTimeBooked(dateStr, s.start, s.end) && !isSlotBlocked(dateStr, s.start, s.end))
      .forEach(s => rows.push({ kind: 'free', key: `f:${s.start}`, start: s.start, end: s.end }));
  }
  dayBlocked.filter(bt => !bt.all_day).forEach(bt => rows.push({ kind: 'blocked', key: `x:${bt.id}`, start: hm(bt.start_time), end: hm(bt.end_time), bt }));
  rows.sort((a, b) => a.start.localeCompare(b.start));

  const freeCount = rows.filter(r => r.kind === 'free').length;
  const blockedCount = rows.filter(r => r.kind === 'blocked').length;
  const summary = blockedAll
    ? `Hela dagen blockerad${blockedAll.reason ? ` · ${blockedAll.reason}` : ''}`
    : !schedule
      ? `Ingen arbetstid på ${DAY_NAMES[date.getDay()].toLowerCase()}ar`
      : [
          `${hm(schedule.start_time)}–${hm(schedule.end_time)}`,
          `${dayBookings.length} ${dayBookings.length === 1 ? 'bokad' : 'bokade'}`,
          `${freeCount} ${freeCount === 1 ? 'ledig' : 'lediga'}`,
          blockedCount > 0 ? `${blockedCount} ${blockedCount === 1 ? 'blockerad' : 'blockerade'}` : null,
        ].filter(Boolean).join(' · ');
  return { dateStr, rows, blockedAll, summary };
}

// Gemensam detaljpanel för en bokning (dagläget och Kommande-listan).
export function BookingDetailPanel({ theme, booking, onOpenClient, onSaveNote, onCancel, onClose, compact }) {
  const [note, setNote] = useState(booking.notes || '');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNote(booking.notes || ''); setConfirming(false); }, [booking.id, booking.notes]);

  const line = theme.card_border;
  const btnPrimary = { background: theme.accent, color: onAccent(theme.accent), border: `1px solid ${theme.accent}` };
  const btnGhost = { background: theme.card_bg, color: NEUTRAL.ink, border: '1px solid #D5DBE4' };
  const btnDanger = { background: theme.card_bg, color: '#B91C1C', border: '1px solid #F3C4C4' };
  const btn = { padding: '9px 14px', minHeight: '40px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };

  const bookedWhen = booking.created_at ? new Date(booking.created_at) : null;
  const name = booking.client?.name || booking.clients?.name || '–';
  const meta = [
    `${hm(booking.start_time)}–${hm(booking.end_time)}`,
    booking.service_title_snapshot || booking.service?.title || null,
    `${minutes(booking.end_time) - minutes(booking.start_time)} min`,
    booking.price_snapshot != null ? `${booking.price_snapshot} kr` : null,
    `bokad av ${booking.booked_by === 'client' ? 'kunden' : 'dig'}${bookedWhen ? ` ${bookedWhen.getDate()} ${MONTHS_SHORT[bookedWhen.getMonth()]}` : ''}`,
  ].filter(Boolean).join(' · ');

  const changed = (note.trim() || '') !== (booking.notes || '');

  return (
    <div className="fr-booking-detail" style={{ borderRadius: '10px', background: '#FBFCFD', border: `1px solid ${line}`, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
      {!compact && (
        <div style={{ width: '40px', height: '40px', borderRadius: '999px', background: theme.accent_soft_bg, color: theme.accent_soft_text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: '800', flexShrink: 0 }}>
          {initials(name)}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
          {!compact && <span style={{ fontSize: '15px', fontWeight: '700', color: NEUTRAL.ink }}>{name}</span>}
          <span style={{ fontSize: '12px', color: NEUTRAL.muted }}>{meta}</span>
        </div>
        <label htmlFor={`note-${booking.id}`} style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', color: NEUTRAL.muted }}>Anteckning</label>
        <textarea
          id={`note-${booking.id}`}
          rows={2}
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Ingen anteckning"
          style={{ fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: '8px', border: '1px solid #D5DBE4', background: theme.card_bg, fontSize: '13px', color: NEUTRAL.ink, resize: 'vertical', outline: 'none' }}
        />
      </div>
      <div className="fr-booking-detail-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, minWidth: '150px' }}>
        {onOpenClient && (booking.client || booking.clients) && (
          <button type="button" onClick={() => onOpenClient(booking.client || booking.clients)} style={{ ...btn, ...btnPrimary }}>Öppna kundkort</button>
        )}
        <button
          type="button"
          disabled={!changed || saving}
          onClick={async () => { setSaving(true); await onSaveNote(booking.id, note); setSaving(false); }}
          style={{ ...btn, ...btnGhost, opacity: changed ? 1 : 0.5, cursor: changed ? 'pointer' : 'default' }}
        >
          {saving ? 'Sparar…' : 'Spara anteckning'}
        </button>
        {confirming ? (
          <div style={{ display: 'flex', gap: '6px' }}>
            <button type="button" onClick={() => onCancel(booking.id)} style={{ ...btn, flex: 1, background: STATUS_COLORS.error_bg, color: '#B91C1C', border: `1px solid ${STATUS_COLORS.error}` }}>Ja, avboka</button>
            <button type="button" onClick={() => setConfirming(false)} style={{ ...btn, ...btnGhost }}>Behåll</button>
          </div>
        ) : (
          <button type="button" onClick={() => setConfirming(true)} style={{ ...btn, ...btnDanger }}>Avboka</button>
        )}
        {onClose && <button type="button" onClick={onClose} style={{ ...btn, background: 'transparent', border: 'none', color: NEUTRAL.muted, fontWeight: '500', minHeight: '32px', padding: '4px 0' }}>Stäng</button>}
      </div>
    </div>
  );
}

// Panel för en vald ledig tid: Boka åt kund / Blockera tiden. Delas med veckovyn (T-14).
// "Blockera tiden" öppnar BlockForm (förälderns ansvar, så formuläret får full bredd).
export function FreeSlotPanel({ theme, label, onBook, onBlockClick, onClose }) {
  const btn = { padding: '9px 14px', minHeight: '40px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  const primary = { ...btn, background: theme.accent, color: onAccent(theme.accent), border: `1px solid ${theme.accent}`, fontWeight: '700' };
  const ghost = { ...btn, background: theme.card_bg, color: NEUTRAL.ink, border: '1px solid #D5DBE4' };
  return (
    <div className="fr-free-panel" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      {label && <span style={{ fontSize: '14px', fontWeight: '700', color: NEUTRAL.ink, marginRight: 'auto' }}>{label}</span>}
      <button type="button" onClick={onBook} style={primary}>Boka åt kund</button>
      <button type="button" onClick={onBlockClick} style={ghost}>Blockera tiden</button>
      {onClose && <button type="button" onClick={onClose} style={{ ...btn, background: 'transparent', border: 'none', color: NEUTRAL.muted, fontWeight: '500' }}>Stäng</button>}
    </div>
  );
}

// T-15: blockera en tid eller hel dag, en gång eller upprepat (var N:e vecka t.o.m. ett datum).
// `plan(repeat, untilStr)` → { ok: [dateStr], conflicts: [{ dateStr, label }] } räknas av Bookings.
const REPEATS = [
  { n: 0, label: 'Nej' },
  { n: 1, label: 'Varje vecka' },
  { n: 2, label: 'Varannan vecka' },
  { n: 3, label: 'Var 3:e vecka' },
  { n: 4, label: 'Var 4:e vecka' },
];
const shortDate = (ds) => { const d = new Date(`${ds}T12:00:00`); return `${DAY_NAMES[d.getDay()].slice(0, 3).toLowerCase()} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`; };
export function BlockForm({ theme, date, start, end, allDay, plan, onSubmit, onCancel }) {
  const dateStr = ymd(date);
  const addDays = (n) => { const d = new Date(date); d.setDate(d.getDate() + n); return ymd(d); };
  const [reason, setReason] = useState('');
  const [repeat, setRepeat] = useState(0);
  const [until, setUntil] = useState(addDays(84));
  const [saving, setSaving] = useState(false);
  const maxUntil = addDays(365);
  const safeUntil = until && until >= dateStr ? (until > maxUntil ? maxUntil : until) : dateStr;
  const p = plan(repeat, safeUntil);
  const count = p.ok.length;
  const weekday = DAY_NAMES[date.getDay()].toLowerCase();
  const what = allDay ? 'hela dagen' : `${start}–${end}`;

  const btn = { padding: '10px 16px', minHeight: '42px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  const chip = (on) => ({ fontFamily: 'inherit', padding: '8px 14px', minHeight: '38px', borderRadius: '999px', fontSize: '13px', cursor: 'pointer', border: on ? `1px solid ${theme.accent}` : '1px solid #D5DBE4', background: on ? theme.accent_soft_bg : theme.card_bg, color: on ? theme.accent_soft_text : NEUTRAL.text, fontWeight: on ? '600' : '400' });
  const label = { fontSize: '13px', fontWeight: '600', color: NEUTRAL.ink };

  return (
    <div className="fr-block-form" style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
      <div style={{ fontSize: '15px', fontWeight: '700', color: NEUTRAL.ink }}>
        Blockera {allDay ? `hela ${weekday}en` : 'tid'} · {DAY_NAMES[date.getDay()].toLowerCase()} {date.getDate()} {MONTHS[date.getMonth()]}{allDay ? '' : ` ${start}–${end}`}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <label htmlFor={`bf-reason-${dateStr}-${start || 'dag'}`} style={label}>Anledning <span style={{ fontWeight: '400', color: NEUTRAL.muted }}>· valfritt, syns bara för dig</span></label>
        <input id={`bf-reason-${dateStr}-${start || 'dag'}`} type="text" value={reason} onChange={e => setReason(e.target.value)} autoFocus style={{ fontFamily: 'inherit', padding: '10px 12px', borderRadius: '8px', border: '1px solid #D5DBE4', fontSize: '14px', maxWidth: '420px', outline: 'none' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={label} id={`bf-rep-${dateStr}-${start || 'dag'}`}>Upprepa</div>
        <div role="radiogroup" aria-labelledby={`bf-rep-${dateStr}-${start || 'dag'}`} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {REPEATS.map(r => (
            <button key={r.n} type="button" role="radio" aria-checked={repeat === r.n} onClick={() => setRepeat(r.n)} style={chip(repeat === r.n)}>{r.label}</button>
          ))}
        </div>
      </div>
      {repeat > 0 && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor={`bf-until-${dateStr}-${start || 'dag'}`} style={label}>Till och med</label>
            <input id={`bf-until-${dateStr}-${start || 'dag'}`} type="date" value={until} min={dateStr} max={maxUntil} onChange={e => setUntil(e.target.value)} style={{ fontFamily: 'inherit', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D5DBE4', fontSize: '14px', maxWidth: '220px' }} />
            <div style={{ fontSize: '12px', color: NEUTRAL.muted }}>Högst 12 månader framåt.</div>
          </div>
          <div style={{ borderRadius: '10px', background: theme.accent_soft_bg, color: theme.accent_soft_text, padding: '10px 12px', fontSize: '13px', fontWeight: '600' }}>
            {count} {count === 1 ? weekday : `${weekday}ar`} · {what} · {shortDate(dateStr)} – {shortDate(safeUntil)}
          </div>
        </>
      )}
      {p.conflicts.length > 0 && (
        <div style={{ borderRadius: '10px', background: STATUS_COLORS.warning_bg, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#92400E' }}>{p.conflicts.length} {p.conflicts.length === 1 ? 'tillfälle har' : 'tillfällen har'} redan en bokning och blockeras inte</div>
          <div style={{ fontSize: '13px', color: '#92400E' }}>{p.conflicts.map(c => c.label).join(' · ')}</div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
        <button type="button" onClick={onCancel} style={{ ...btn, background: theme.card_bg, color: NEUTRAL.ink, border: '1px solid #D5DBE4' }}>Avbryt</button>
        <button
          type="button"
          disabled={saving || count === 0}
          onClick={async () => { setSaving(true); await onSubmit({ reason: reason.trim() || null, repeat, until: safeUntil }); setSaving(false); }}
          style={{ ...btn, background: theme.accent, color: onAccent(theme.accent), border: `1px solid ${theme.accent}`, fontWeight: '700', opacity: saving || count === 0 ? 0.5 : 1 }}
        >
          {saving ? 'Blockerar…' : repeat > 0 ? `Blockera ${count} tillfällen` : allDay ? 'Blockera dagen' : 'Blockera tiden'}
        </button>
      </div>
    </div>
  );
}

function BookingDayBand({
  theme,
  date,
  schedule,
  rows,
  blockedAll,
  blockingDay,
  onCancelBlockDay,
  openClientPicker,
  planBlock,
  blockSeries,
  unblockTime,
  unblockSeries,
  cancelBooking,
  saveNote,
  onOpenClient,
  nav, // valfritt: { title, onPrev, onNext } – egen rubrikrad (används i Kommande)
}) {
  const [openKey, setOpenKey] = useState(null);
  const [blockKey, setBlockKey] = useState(null); // ledig tid som håller på att blockeras
  const [confirmSeries, setConfirmSeries] = useState(null); // series_id som ska tas bort
  const dateStr = ymd(date);
  useEffect(() => { setOpenKey(null); setBlockKey(null); setConfirmSeries(null); }, [dateStr]);

  const now = new Date();
  const isToday = dateStr === ymd(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const isPastDay = dateStr < ymd(now);
  const nextRow = isToday ? rows.find(r => r.kind === 'booking' && minutes(r.start) > nowMin) : null;

  const chip = (bg, color, text, extra = {}) => (
    <span style={{ display: 'inline-block', marginTop: '6px', background: bg, color, padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', ...extra }}>{text}</span>
  );
  const btn = { padding: '9px 14px', minHeight: '40px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  const ghost = { ...btn, background: theme.card_bg, color: NEUTRAL.ink, border: '1px solid #D5DBE4' };
  const rowBase = { display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 20px', borderTop: `1px solid ${theme.card_border}` };
  const timeCol = { width: '104px', flexShrink: 0 };

  return (
    <div className="fr-day-agenda" style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden' }}>
      {nav && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px' }}>
          <button type="button" aria-label="Föregående dag" onClick={nav.onPrev} style={{ ...ghost, width: '40px', padding: 0 }}>←</button>
          <h2 style={{ margin: 0, flex: 1, fontSize: '16px', fontWeight: '800', fontFamily: 'var(--font-heading)', color: NEUTRAL.ink }}>{nav.title}</h2>
          <button type="button" aria-label="Nästa dag" onClick={nav.onNext} style={{ ...ghost, width: '40px', padding: 0 }}>→</button>
        </div>
      )}

      {blockingDay && !blockedAll && (
        <div style={{ ...rowBase, borderTop: nav ? rowBase.borderTop : 'none', background: '#FBFCFD' }}>
          <BlockForm
            theme={theme}
            date={date}
            allDay
            plan={(repeat, until) => planBlock({ dateStr, allDay: true }, repeat, until)}
            onSubmit={async (o) => { await blockSeries({ dateStr, allDay: true }, o); onCancelBlockDay(); }}
            onCancel={onCancelBlockDay}
          />
        </div>
      )}

      {blockedAll ? (
        <div style={{ ...rowBase, borderTop: nav ? rowBase.borderTop : 'none', background: `repeating-linear-gradient(135deg, ${theme.card_bg} 0 8px, ${NEUTRAL.page} 8px 16px)` }}>
          {chip(NEUTRAL.ink, '#FFFFFF', 'Hela dagen blockerad', { marginTop: 0 })}
          <span style={{ flex: 1, fontSize: '13px', color: NEUTRAL.text }}>{blockedAll.reason || 'Ingen anledning angiven'}{blockedAll.series_id ? ' · ↻ upprepas' : ''}</span>
          <button type="button" onClick={() => unblockTime(blockedAll.id)} style={ghost}>{blockedAll.series_id ? 'Ta bort detta tillfälle' : 'Avblockera dagen'}</button>
          {blockedAll.series_id && (confirmSeries === blockedAll.series_id ? (
            <button type="button" onClick={async () => { await unblockSeries(blockedAll.series_id); setConfirmSeries(null); }} style={{ ...btn, background: STATUS_COLORS.error_bg, color: '#B91C1C', border: `1px solid ${STATUS_COLORS.error}` }}>Ja, ta bort kommande</button>
          ) : (
            <button type="button" onClick={() => setConfirmSeries(blockedAll.series_id)} style={{ ...btn, background: theme.card_bg, color: '#B91C1C', border: '1px solid #F3C4C4' }}>Ta bort hela serien</button>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div style={{ ...rowBase, borderTop: nav ? rowBase.borderTop : 'none', fontSize: '13px', color: NEUTRAL.muted }}>
          {!schedule ? `Ingen arbetstid inställd för ${DAY_NAMES[date.getDay()].toLowerCase()}ar. Lägg till under Veckoschema.` : 'Inga tider den här dagen.'}
        </div>
      ) : (
        rows.map((r, i) => {
          const first = i === 0 && !nav && !blockingDay;
          const base = { ...rowBase, borderTop: first ? 'none' : rowBase.borderTop };
          if (r.kind === 'free') {
            const past = isPastDay || (isToday && minutes(r.start) < nowMin);
            return (
              <div key={r.key} style={{ borderTop: base.borderTop, background: blockKey === r.key ? '#FBFCFD' : 'transparent' }}>
                <div className="fr-day-row" style={{ ...base, borderTop: 'none', opacity: past ? 0.55 : 1 }}>
                  <div style={timeCol}>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: NEUTRAL.text }}>{r.start}–{r.end}</div>
                    {chip('transparent', NEUTRAL.muted, 'Ledig', { border: `1px dashed ${NEUTRAL.faint}`, padding: '2px 9px' })}
                  </div>
                  <div style={{ flex: 1, fontSize: '13px', color: NEUTRAL.muted }}>{past ? 'Passerad' : 'Ingen bokning'}</div>
                  {!past && blockKey !== r.key && (
                    <div className="fr-day-row-actions">
                      <FreeSlotPanel
                        theme={theme}
                        onBook={() => openClientPicker(dateStr, r.start, r.end)}
                        onBlockClick={() => setBlockKey(r.key)}
                      />
                    </div>
                  )}
                </div>
                {blockKey === r.key && (
                  <div style={{ padding: '0 20px 16px' }}>
                    <BlockForm
                      theme={theme}
                      date={date}
                      start={r.start}
                      end={r.end}
                      plan={(repeat, until) => planBlock({ dateStr, start: r.start, end: r.end }, repeat, until)}
                      onSubmit={async (o) => { await blockSeries({ dateStr, start: r.start, end: r.end }, o); setBlockKey(null); }}
                      onCancel={() => setBlockKey(null)}
                    />
                  </div>
                )}
              </div>
            );
          }
          if (r.kind === 'blocked') {
            return (
              <div key={r.key} className="fr-day-row" style={{ ...base, background: `repeating-linear-gradient(135deg, ${theme.card_bg} 0 8px, ${NEUTRAL.page} 8px 16px)` }}>
                <div style={timeCol}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: NEUTRAL.text }}>{r.start}–{r.end}</div>
                  {chip(NEUTRAL.ink, '#FFFFFF', 'Blockerad')}
                </div>
                <div style={{ flex: 1, minWidth: 0, fontSize: '13px', color: NEUTRAL.text }}>
                  {r.bt.reason || 'Ingen anledning angiven'}
                  {r.bt.series_id && <div style={{ fontSize: '12px', color: NEUTRAL.muted, marginTop: '2px' }}>↻ Del av en upprepad blockering</div>}
                </div>
                {r.bt.series_id ? (
                  <div className="fr-day-row-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {confirmSeries === r.bt.series_id ? (
                      <>
                        <button type="button" onClick={async () => { await unblockSeries(r.bt.series_id); setConfirmSeries(null); }} style={{ ...btn, background: STATUS_COLORS.error_bg, color: '#B91C1C', border: `1px solid ${STATUS_COLORS.error}` }}>Ja, ta bort kommande</button>
                        <button type="button" onClick={() => setConfirmSeries(null)} style={ghost}>Behåll</button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => unblockTime(r.bt.id)} style={ghost}>Ta bort detta tillfälle</button>
                        <button type="button" onClick={() => setConfirmSeries(r.bt.series_id)} style={{ ...btn, background: theme.card_bg, color: '#B91C1C', border: '1px solid #F3C4C4' }}>Ta bort hela serien</button>
                      </>
                    )}
                  </div>
                ) : (
                  <button type="button" onClick={() => unblockTime(r.bt.id)} style={ghost}>Avblockera</button>
                )}
              </div>
            );
          }
          const b = r.b;
          const name = b.client?.name || b.clients?.name || '–';
          const service = b.service_title_snapshot || b.service?.title || null;
          const isDone = isPastDay || (isToday && minutes(r.end) <= nowMin);
          const isOngoing = isToday && minutes(r.start) <= nowMin && minutes(r.end) > nowMin;
          const isNext = nextRow?.key === r.key;
          const open = openKey === r.key;
          const status = isOngoing
            ? chip(theme.accent, onAccent(theme.accent), 'Pågår nu')
            : isNext ? chip(theme.accent_soft_bg, theme.accent_soft_text, 'Nästa')
            : isDone ? chip(NEUTRAL.page, NEUTRAL.muted, 'Klar')
            : chip(theme.accent_soft_bg, theme.accent_soft_text, 'Bokad');
          return (
            <div key={r.key} style={{ borderTop: base.borderTop, background: open ? '#FBFCFD' : theme.card_bg }}>
              <button
                type="button"
                className="fr-day-row"
                aria-expanded={open}
                onClick={() => setOpenKey(open ? null : r.key)}
                style={{ ...rowBase, borderTop: 'none', width: '100%', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <div style={timeCol}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: NEUTRAL.ink }}>{r.start}–{r.end}</div>
                  {status}
                </div>
                <div style={{ width: '40px', height: '40px', borderRadius: '999px', background: theme.accent_soft_bg, color: theme.accent_soft_text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: '800', flexShrink: 0 }}>{initials(name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: NEUTRAL.ink }}>{name}</div>
                  <div style={{ fontSize: '13px', color: NEUTRAL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[service, `${minutes(r.end) - minutes(r.start)} min`, b.price_snapshot != null ? `${b.price_snapshot} kr` : null].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <span aria-hidden="true" style={{ color: NEUTRAL.faint, fontSize: '14px' }}>{open ? '▴' : '▾'}</span>
              </button>
              {open && (
                <div style={{ padding: '0 20px 16px' }}>
                  <BookingDetailPanel
                    theme={theme}
                    compact
                    booking={b}
                    onOpenClient={onOpenClient}
                    onSaveNote={saveNote}
                    onCancel={async (id) => { await cancelBooking(id); setOpenKey(null); }}
                  />
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

export default BookingDayBand;
